import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';
import ky from 'ky';
import { config } from '../config';
import queryClient from '../lib/queryClient';

interface User {
  uuid: string;
  sessionUuid: string;
  isAccountLocked: boolean;
  source: string;
  datacenters: string[];
  isSource: boolean;
  emailAddress: string;
  lastLogin: Date;
  hasPassword: boolean;
  needsPasswordReset: boolean;
  features: string[];
}

interface Payload {
  type: string;
  access: string;
  user: User;
  impersonationSubject?: User;
}

export interface TokenContent {
  payload: Payload;
  iat: number; // Issue At Time (seconds)
  exp: number; // Expiration Time (seconds)
  iss: string;
}

interface TokenResponse {
  jwt: string;
  payload: Payload;
}

export const tokenName = import.meta.env.VITE_TOKEN_NAME;

/**
 * Gets the token from local storage.
 * For use internally in the exposed getToken function and useToken hook.
 */
function _getToken() {
  const params = new URLSearchParams(window.location.search);
  const authToken = params.get('authToken');
  const tokenFromParams = authToken ?? params.get('authorization');

  let token = localStorage.getItem(tokenName);

  // if a token is passed as a param, always attempt to honor it first
  if (tokenFromParams) {
    _setToken(tokenFromParams);

    params.delete('authorization');
    params.delete('authToken');

    const hasParams = params.size > 0;
    const newUrl = hasParams
      ? window.location.protocol + '//' + window.location.host + window.location.pathname + '?' + params.toString()
      : window.location.protocol + '//' + window.location.host + window.location.pathname;

    // Change the URL without reloading the page
    history.replaceState({}, '', newUrl);

    token = tokenFromParams;
  }

  return token;
}

/**
 * Gets the token content from local storage.
 * For use internally in the exposed getTokenContent function and useToken hook.
 */
function _getTokenContent() {
  const token = _getToken();
  const tokenContent = token ? (jwtDecode(token) as TokenContent) : null;

  return tokenContent;
}

/**
 * Sets the token in local storage.
 * For use internally in the exposed setToken function and useToken hook.
 * @param token The token to set in local storage. If null, the token is removed from local storage.
 */
function _setToken(token: string | null) {
  if (!token) {
    localStorage.removeItem(tokenName);
  } else {
    localStorage.setItem(tokenName, token);
  }
}

/**
 * Gets the token from local storage.
 * The token is cached.
 */
export const getToken = () => {
  // First check localStorage to ensure we're not using stale cache after logout
  const localStorageToken = _getToken();

  // Get cached token
  let token: string | null = null;
  token = queryClient.getQueryData<string | undefined>([tokenName]) ?? null;

  // If localStorage is empty but cache has a token, clear the cache
  if (!localStorageToken && token) {
    queryClient.setQueryData<string | null>([tokenName], null);
    return null;
  }

  // If we have a cached token that matches localStorage, use it
  if (token === localStorageToken) {
    return token;
  }

  // Otherwise, update cache with localStorage value
  token = localStorageToken;
  queryClient.setQueryData<string | null>([tokenName], token);
  return token;
};

/**
 * Gets the token content from local storage.
 * The token content is cached.
 * The cache is invalidated when the token is set.
 */
export const getTokenContent = () => {
  let tokenContent: TokenContent | null = null;

  tokenContent = queryClient.getQueryData<TokenContent | undefined>([tokenName, 'content']) ?? null;
  const tokenQuery = queryClient.getQueryCache().find({
    queryKey: [tokenName, 'content'],
  });
  if (tokenContent && tokenQuery?.state.isInvalidated === false) {
    return tokenContent;
  }

  tokenContent = _getTokenContent();
  queryClient.setQueryData<TokenContent | null>([tokenName, 'content'], tokenContent);

  return tokenContent;
};

/**
 * Sets the token in local storage.
 * The token is also updated/removed from the query cache so getToken() and API requests use it immediately.
 * @param token The token to set in local storage. If null, the token is removed from local storage.
 */
export const setToken = (token: string | null) => {
  _setToken(token);

  if (token) {
    queryClient.setQueryData([tokenName], token);
    queryClient.removeQueries({ queryKey: [tokenName, 'content'] });
    queryClient.invalidateQueries({ queryKey: [tokenName] });
  } else {
    // When logging out, immediately set the cache to null to prevent stale reads
    queryClient.setQueryData([tokenName], null);
    queryClient.setQueryData([tokenName, 'content'], null);
    queryClient.removeQueries({ queryKey: [tokenName] });
    queryClient.removeQueries({ queryKey: [tokenName, 'content'] });
  }
};

/**
 * Private client for token refresh that doesn't redirect on auth failures.
 *
 * This is the ONLY place where we don't use the standard apiClient() because:
 * 1. The refresh endpoint needs to receive expired tokens to validate and refresh them
 * 2. We can't redirect to login when the token is expired (that would create a loop)
 * 3. The refresh endpoint has its own auth error handling
 *
 * DO NOT export this or use it for any other purpose.
 */
const createRefreshClient = () => {
  return ky.create({
    prefixUrl: config.GATEWAY_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
    hooks: {
      beforeRequest: [
        (request) => {
          const token = getToken();
          if (token) {
            request.headers.set('Authorization', `Bearer ${token}`);
          }
        },
      ],
    },
  });
};

/**
 * Refreshes the currently used token and updates it in local storage
 */
export const refreshToken = async () => {
  const client = createRefreshClient();
  const response = await client.post('api/c2fo/refresh-token').json<TokenResponse>();

  setToken(response.jwt);
};

/**
 * Gets the current auth user from the token content based on impersonation.
 */
export const getCurrentAuthUser = () => {
  const tokenContent = getTokenContent();
  return tokenContent?.payload.impersonationSubject ?? tokenContent?.payload.user;
};

/**
 * Gets the current user's initials from the token (e.g. "JD" for john.doe@c2fo.com).
 */
export const getUserInitials = (): string => {
  const user = getCurrentAuthUser();
  const email = user?.emailAddress;
  if (!email) {
    return 'U';
  }

  const parts = email.split('@')[0].split('.');
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  return email.substring(0, 2).toUpperCase();
};

/**
 * Gets the current user's email from the token.
 */
export const getUserEmail = (): string | null => {
  const user = getCurrentAuthUser();
  return user?.emailAddress ?? null;
};

/**
 * Clears the token and redirects. Use a full URL to log out of legacy admin first
 * so the user is not re-authenticated when landing on new admin login.
 * @param redirectTo - Full URL to go to after clearing token (e.g. legacy logout URL). Defaults to '/'.
 */
export const logout = (redirectTo?: string): void => {
  // Clear the token first
  setToken(null);

  // Cancel all in-flight queries to prevent them from using the old token
  queryClient.cancelQueries();

  // Clear all cached data
  queryClient.clear();

  // Redirect after cleanup
  window.location.href = redirectTo ?? '/';
};

/**
 * Returns if the current session is impersonated.
 * For use internally in the isImpersonationSession hook.
 */
export const isImpersonationSession = () => {
  const tokenContent = getTokenContent();
  return !!tokenContent?.payload.impersonationSubject;
};

/**
 * Provides access to the token and token content.
 * Also provides a function to set the token.
 * Both token and token content are cached.
 * The cache is invalidated when the token is set.
 */
export function useToken() {
  const { data: token, ...tokenResult } = useSuspenseQuery({
    queryKey: [tokenName],
    queryFn: () => _getToken(),
  });

  const { data: tokenContent, ...tokenContentResult } = useSuspenseQuery({
    queryKey: [tokenName, 'content'],
    queryFn: () => _getTokenContent(),
  });

  const { mutate: setTokenFn, ...setTokenResult } = useMutation({
    mutationFn: async (token: string | null) => _setToken(token),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [tokenName],
      });
    },
  });

  return {
    token,
    tokenResult,
    tokenContent,
    tokenContentResult,
    setToken: setTokenFn,
    setTokenResult,
    isImpersonationSession: !!tokenContent?.payload.impersonationSubject,
    isC2foUserSession: !!tokenContent?.payload?.user?.emailAddress.includes('@c2fo.com'),
  };
}

export default getToken;
